// Slice H7.1 — minimal client-side theme module.
//
// The rest of the PWA reads `localStorage.cf_theme` (a JSON-stringified
// "light" | "dark") and recomputes its own color palette per render via
// `useState(()=>ld("cf_theme","light"))`. The Hermes panel was the lone
// holdout — it shipped with hardcoded `rgb(...)` literals from H5 and
// rendered dark regardless of cf_theme. This module gives Hermes (and the
// new H7.1 analytics tile) a single token map keyed by mode + a small
// subscribe/notify primitive so panels re-render on theme change without
// having to plumb the parent app's `theme` state through every prop.
//
// Why a token map instead of CSS variables? The rest of the codebase
// uses inline-style + per-component palette objects (the `S` and `C`
// const-objects in app.jsx and HermesPanel.jsx). Migrating everything to
// :root CSS vars would be a multi-slice refactor; matching the existing
// inline-style pattern keeps blast radius scoped to Hermes for H7.1.
//
// Mode resolution order:
//   1. Explicit cf_theme in localStorage ("light" or "dark") wins.
//   2. Otherwise, prefers-color-scheme system preference.
//   3. Final fallback: "light" (matches the app's default for new users).
//
// Notification sources:
//   - "storage" event   — fires for cross-tab changes to localStorage.
//   - "cf_theme_change" — custom event the in-app theme toggle dispatches
//                         after writing cf_theme (storage events do NOT
//                         fire in the same window that wrote them).
//   - prefers-color-scheme media-query change (when no explicit cf_theme).

// Tokens are hex strings (not rgb()) wherever they're likely to be
// concatenated with a transparency suffix at the call site (e.g.
// `C.err + "22"` for an 8-digit-hex tinted background). The two `border*`
// keys stay in rgba() form because they're only ever used as
// `1px solid ${C.border}` borders, never as backgrounds.
// UI refresh (2026-10): values track the app THEMES (neutral zinc + one
// accent) so Hermes, Co-work, Projects and the Guide match the rest of the app.
const TOKENS = Object.freeze({
  light: Object.freeze({
    surface:        '#ffffff',
    surface_alt:    '#f7f7f8',
    surface_raised: 'rgba(255, 255, 255, 0.95)',
    border_subtle:  'rgba(24, 24, 27, 0.08)',
    border:         'rgba(24, 24, 27, 0.14)',
    text_primary:   '#18181b', // zinc-900
    text_muted:     '#52525b', // zinc-600
    text_disabled:  '#71717a', // zinc-500
    accent:         '#2f6bd8',
    accent_alt:     '#2563eb', // blue-600
    accent_fg:      '#ffffff',
    // Light-mode status colors are darker variants of the dark-mode
    // versions so they keep ≥ 4.5:1 contrast on a white surface (Tailwind
    // green-700 / amber-700 / red-700 / violet-700 / slate-500).
    danger:         '#b91c1c', // red-700
    success:        '#15803d', // green-700
    warn:           '#b45309', // amber-700
    capped:         '#6d28d9', // violet-700
    cancelled:      '#71717a', // zinc-500
  }),
  dark: Object.freeze({
    surface:        '#111113',
    surface_alt:    '#18181b',
    surface_raised: 'rgba(24, 24, 27, 0.95)',
    border_subtle:  '#26262b',
    border:         '#34343a',
    text_primary:   '#ededf0',
    text_muted:     '#a1a1aa',
    text_disabled:  '#8b8b94',
    accent:         '#6ea8fe',
    accent_alt:     '#4f8cf0',
    accent_fg:      '#ffffff',
    danger:         '#f87171',
    success:        '#34d399',
    warn:           '#d97706',
    capped:         '#8b5cf6',
    cancelled:      '#71717a',
  }),
});

// Read mode, in priority order. Pure function (no side effects), safe
// to call from anywhere — including SSR-style codepaths where window
// might not exist (we guard each access).
function readMode() {
  // 1) Explicit cf_theme value (JSON-encoded in localStorage by app.jsx).
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem('cf_theme');
      if (raw != null) {
        // App.jsx stores it via `sv()` which JSON.stringify's — i.e.
        // localStorage value is `"light"` (with quotes). Accept either
        // form so a hand-set `cf_theme=light` (no quotes) also works.
        let v = raw;
        try { v = JSON.parse(raw); } catch { /* fall through to raw */ }
        if (v === 'light' || v === 'dark') return v;
      }
    }
  } catch (_) { /* localStorage may throw in privacy modes; ignore */ }
  // 2) System preference.
  try {
    if (typeof window !== 'undefined' && window.matchMedia) {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    }
  } catch (_) { /* ignore */ }
  // 3) Final fallback — matches the app default.
  return 'light';
}

// Subscriber set + notify primitive. Each subscriber is a zero-arg
// callback the consumer uses to trigger its own re-render (typically
// a useReducer/useState bump).
const subs = new Set();
function subscribe(fn) {
  if (typeof fn !== 'function') return () => {};
  subs.add(fn);
  return () => { subs.delete(fn); };
}
function notifyAll() {
  // Snapshot the set first so a subscriber that unsubscribes during the
  // notify pass doesn't break iteration.
  for (const fn of Array.from(subs)) {
    try { fn(); } catch (_) { /* a subscriber throwing must not block others */ }
  }
}

// Wire the three notification sources, but only once and only when a
// real `window` exists (lets node:test import the module without crashing
// on missing globals).
let _wired = false;
function _wireOnce() {
  if (_wired || typeof window === 'undefined') return;
  _wired = true;
  // Cross-tab change.
  try {
    window.addEventListener('storage', (e) => {
      if (e && e.key === 'cf_theme') notifyAll();
    });
  } catch (_) { /* ignore */ }
  // Same-window change (app.jsx's setTheme effect dispatches this).
  try {
    window.addEventListener('cf_theme_change', () => notifyAll());
  } catch (_) { /* ignore */ }
  // System preference change (only meaningful when no explicit cf_theme).
  try {
    if (window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      // Older Safari uses addListener; modern browsers use addEventListener.
      const handler = () => notifyAll();
      if (mq.addEventListener) mq.addEventListener('change', handler);
      else if (mq.addListener)  mq.addListener(handler);
    }
  } catch (_) { /* ignore */ }
}
_wireOnce();

// Programmatic mode setter — writes to localStorage and notifies
// same-window subscribers.
//
// Notification path: prefer dispatching the `cf_theme_change` window
// event (the same channel app.jsx's setTheme effect uses, so external
// listeners stay consistent), but fall back to a direct notifyAll()
// when no window is present (e.g. unit tests run under raw Node). We
// never do BOTH — the wired listener would call notifyAll a second
// time and subscribers would fire twice per setMode call.
function setMode(next) {
  if (next !== 'light' && next !== 'dark') return;
  try {
    if (typeof localStorage !== 'undefined') {
      // Match the app's existing JSON-string format so we don't fight
      // the existing setTheme effect.
      localStorage.setItem('cf_theme', JSON.stringify(next));
    }
  } catch (_) { /* ignore */ }
  let dispatched = false;
  try {
    if (_wired && typeof window !== 'undefined' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('cf_theme_change'));
      dispatched = true;
    }
  } catch (_) { /* ignore */ }
  if (!dispatched) notifyAll();
}

function tokens() { return TOKENS[readMode()]; }

// Test-only: reset internal state. Not part of the public surface but
// exposed via the underscore prefix so the cf_theme test file can clear
// subscribers between cases.
function _resetForTests() { subs.clear(); }

export {
  readMode,
  tokens,
  subscribe,
  setMode,
  TOKENS as _TOKENS,
  _resetForTests,
};
